import ArqNavigation from "@/components/arqera/ArqNavigation";
import ArqFooter from "@/components/arqera/ArqFooter";
import { SEO } from "@/components/SEO";
import { Search, Wrench, ShieldCheck, Check, FileText, GitBranch, ClipboardList } from "lucide-react";

const phases = [
  {
    icon: Search,
    tag: "Phase 1",
    title: "Assess",
    summary:
      "We identify what is exposed, what is misconfigured, and what is actively violating your compliance posture.",
    steps: [
      "Scoping interview and asset enumeration — applications, AI surfaces, third-party data flows, consent surfaces",
      "Threat modeling against the actual environment, not a generic checklist",
      "Hands-on testing: SAST/SCA, API and authorization analysis, AI red-team probes, pre/post-consent tracking audit",
      "Regulatory mapping per finding — HIPAA, SOC 2, NIST AI RMF, CPRA/CCPA, GDPR, CMMC, HITRUST",
      "Risk rating with business impact and legal exposure framing",
    ],
    deliverable: "Findings report with prioritized remediation roadmap and regulatory citation per finding.",
  },
  {
    icon: Wrench,
    tag: "Phase 2",
    title: "Remediate",
    summary:
      "We fix what we find — configuration, code, platform, GTM, and consent management — and document every change.",
    steps: [
      "Remediation plan with owner, dependency, and target close date per finding",
      "Hands-on implementation: code-level fixes, IAM and platform changes, GTM and CMP reconfiguration, secrets and key rotation",
      "Pre-production validation in staging with rollback plan",
      "Controlled rollout to production with monitoring and abort criteria",
      "Change log, commit history, and configuration diff captured per change",
    ],
    deliverable: "Remediated implementation with full change log mapped back to original findings.",
  },
  {
    icon: ShieldCheck,
    tag: "Phase 3",
    title: "Validate",
    summary:
      "We prove it is working — independently, in production, and in a form an auditor or carrier will accept.",
    steps: [
      "Independent re-test against original findings with pass/fail per item",
      "Evidence package assembly: test artifacts, screenshots, beacon logs, configuration exports",
      "Rescan coordination with the originating party (insurance carrier, auditor, regulator)",
      "Executive and board-ready summary with regulatory and financial framing",
      "Optional: enrollment in continuous monitoring and quarterly re-validation",
    ],
    deliverable: "Validation report and evidence package suitable for regulatory, insurance, or board-level review.",
  },
];

const principles = [
  {
    icon: FileText,
    title: "Documented, not improvised",
    body: "Every engagement runs against a written playbook. Templates, evidence formats, and checklists are versioned firm IP — not artifacts that live in any single practitioner's head.",
  },
  {
    icon: GitBranch,
    title: "Reproducible, not bespoke",
    body: "Two practitioners running the same engagement on the same environment produce comparable outputs. Findings are graded against a fixed rubric; remediations follow standard patterns.",
  },
  {
    icon: ClipboardList,
    title: "Audit-ready by default",
    body: "Outputs are structured for the people who will read them next — auditors, insurance carriers, regulators, boards. We do not produce PDFs that need translation.",
  },
];

export default function Methodology() {
  return (
    <div className="min-h-screen bg-arq-bg text-arq-text">
      <SEO
        title="Methodology — ArqeraAI | Assess. Remediate. Validate."
        description="The ArqeraAI engagement methodology: a documented, reproducible, three-phase process for closing AI, application, and privacy risk with audit-ready evidence."
        canonical="https://arqeraai.com/methodology"
      />
      <ArqNavigation />

      <main className="pt-32 pb-20 px-6">
        <div className="max-w-[1100px] mx-auto">
          <div className="arq-label mb-5">Methodology</div>
          <h1
            className="font-display font-semibold text-arq-text mb-6"
            style={{ fontSize: "clamp(32px, 5vw, 52px)", lineHeight: 1.1 }}
          >
            A documented, reproducible engagement model.{" "}
            <span className="text-arq-blue">Not a consultant in a room.</span>
          </h1>
          <p className="text-arq-muted text-[17px] leading-[1.75] max-w-[820px] mb-16">
            Every ArqeraAI engagement runs against the same three-phase methodology — Assess,
            Remediate, Validate. Templates, evidence formats, and grading rubrics are firm-level
            IP, applied identically across practitioners and engagements. The output is the same
            kind of document an auditor, insurance carrier, or regulator will accept on the first
            read.
          </p>

          {/* Phases */}
          <div className="space-y-6 mb-20">
            {phases.map((p) => (
              <div key={p.title} className="arq-card p-8 md:p-10">
                <div className="grid md:grid-cols-[200px_1fr] gap-8">
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-md bg-arq-blue/10 border border-arq-blue/30 flex items-center justify-center">
                        <p.icon className="w-5 h-5 text-arq-blue" />
                      </div>
                      <div className="font-mono-tech text-[11px] tracking-[0.12em] uppercase text-arq-tertiary">
                        {p.tag}
                      </div>
                    </div>
                    <h2 className="font-display text-[28px] font-semibold text-arq-text mb-3">
                      {p.title}
                    </h2>
                    <p className="text-arq-muted text-[14px] leading-[1.6]">{p.summary}</p>
                  </div>
                  <div>
                    <div className="font-mono-tech text-[11px] tracking-[0.1em] uppercase text-arq-tertiary mb-3">
                      What Happens
                    </div>
                    <ul className="space-y-2.5 mb-6">
                      {p.steps.map((s) => (
                        <li key={s} className="flex items-start gap-3 text-[15px] text-arq-text/90 leading-[1.6]">
                          <Check className="w-4 h-4 mt-1 flex-shrink-0 text-arq-teal" />
                          <span>{s}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="pt-5 border-t border-arq-border">
                      <div className="font-mono-tech text-[11px] tracking-[0.1em] uppercase text-arq-tertiary mb-1.5">
                        Deliverable
                      </div>
                      <div className="text-arq-text text-[15px] leading-[1.6]">{p.deliverable}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Principles */}
          <div className="mb-20">
            <div className="arq-label mb-4">Operating Principles</div>
            <h2
              className="font-display font-semibold text-arq-text mb-10 max-w-[820px]"
              style={{ fontSize: "clamp(24px, 3vw, 32px)", lineHeight: 1.25 }}
            >
              How we make a methodology, and not a personality, the unit of value.
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {principles.map((p) => (
                <div key={p.title} className="arq-card p-7">
                  <div className="w-10 h-10 rounded-md bg-arq-teal/10 border border-arq-teal/30 flex items-center justify-center mb-4">
                    <p.icon className="w-5 h-5 text-arq-teal" />
                  </div>
                  <h3 className="font-display text-[18px] font-semibold text-arq-text mb-2">
                    {p.title}
                  </h3>
                  <p className="text-arq-muted text-[14px] leading-[1.7]">{p.body}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="arq-card p-8 md:p-10 text-center">
            <h3 className="font-display text-[24px] md:text-[28px] font-semibold text-arq-text mb-3">
              Bring us a finding. We will scope the engagement.
            </h3>
            <p className="text-arq-muted text-[15px] mb-6 max-w-[560px] mx-auto">
              Audit finding, insurance scan, regulatory letter, or pre-procurement security
              questionnaire. Written proposal within 48 hours.
            </p>
            <a
              href="/#contact"
              className="inline-flex items-center justify-center bg-arq-blue text-white font-semibold text-[15px] px-7 py-3.5 rounded-md hover:bg-arq-blue-bright transition-colors"
            >
              Schedule Assessment
            </a>
          </div>
        </div>
      </main>

      <ArqFooter />
    </div>
  );
}
